import { Check, Search, X } from 'lucide-react'
import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'

import ProfileAvatarURL from 'src/shared/features/users/components/profile-photos/profile-avatar-url'
import { useDebouncedValue } from 'src/shared/hooks/use-debounced-value'
import type { EnterpriseCompanyTypeValues } from 'src/shared/schemas/forms/auth'
import { cn } from 'src/shared/utils/cn'
import { ScrollBarTailwindClassesEnterprise } from 'src/shared/utils/tailwind-styles'

import useGetEnterpriseMembers from '../../../shared/features/enterprise/api/use-get-enterprise-members'

export function getMemberPluralLabel(companyType: EnterpriseCompanyTypeValues | undefined): string {
  if (!companyType) return 'member'
  if (companyType === 'mortgage-lender' || companyType === 'mortgage-brokerage') return 'loan officer'
  if (companyType === 'real-estate-brokerage') return 'agent'
  return 'member'
}

type LeadDistributionMemberPickerProps = {
  enterpriseId: number
  allMemberIds?: number[]
  companyType: EnterpriseCompanyTypeValues | undefined
  selectedUserIds: number[]
  onSelectedUserIdsChange: (userIds: number[]) => void
}

function MemberListInner({
  enterpriseId,
  searchValue,
  selectedUserIds,
  onToggleUserId,
  onLoadedUserIdsChange,
}: {
  enterpriseId: number
  searchValue: string
  selectedUserIds: number[]
  onToggleUserId: (userId: number) => void
  onLoadedUserIdsChange: (userIds: number[]) => void
}) {
  const debouncedSearchValue = useDebouncedValue(searchValue, 250)
  const { ref: endOfListRef, inView: isEndOfListInView } = useInView()

  const {
    data: membersData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetEnterpriseMembers({
    enterpriseId,
    sortBy: 'full_name',
    sortOrder: 'asc',
    limit: 10,
    search: debouncedSearchValue.trim() || undefined,
  })

  const members = useMemo(() => {
    const flattened = membersData?.pages.flatMap((page) => page?.results?.memberships ?? []) ?? []
    const seenUserIds = new Set<number>()
    return flattened.filter((member) => {
      if (!member?.userId || seenUserIds.has(member.userId)) return false
      seenUserIds.add(member.userId)
      return true
    })
  }, [membersData?.pages])

  const loadedUserIds = useMemo(() => members.map((m) => m.userId).filter((id): id is number => id != null), [members])

  useEffect(() => {
    onLoadedUserIdsChange(loadedUserIds)
  }, [loadedUserIds, onLoadedUserIdsChange])

  useEffect(() => {
    if (isEndOfListInView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [fetchNextPage, hasNextPage, isEndOfListInView, isFetchingNextPage])

  const selectedSet = useMemo(() => new Set(selectedUserIds), [selectedUserIds])

  if (members.length === 0) {
    return <p className="py-1 text-[13px] font-medium leading-none tracking-[-0.13px] text-muted">No members found.</p>
  }

  return (
    <div className={cn('flex flex-col overflow-y-auto pr-3', ScrollBarTailwindClassesEnterprise)}>
      {members.map((member, index) => {
        const userId = member.userId
        if (!userId) return null
        const isSelected = selectedSet.has(userId)
        return (
          <React.Fragment key={member.id}>
            <button
              type="button"
              onClick={() => onToggleUserId(userId)}
              className="flex w-full items-center justify-between gap-3 rounded-xl bg-transparent py-[10px] text-left transition-colors hover:bg-white/60"
            >
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <ProfileAvatarURL
                  photoUrl={member.profilePhoto}
                  size={42}
                  className="size-[42px] shrink-0 rounded-full border-2 border-white shadow-[0px_1px_10px_0px_rgba(27,48,41,0.1)]"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[15px] font-semibold tracking-tight text-primary">
                    {member.fullName ?? 'Unknown'}
                  </p>
                  <p className="truncate text-[13px] font-medium text-muted">{member.email ?? ''}</p>
                </div>
              </div>
              <span
                className={cn(
                  'flex size-[25px] shrink-0 items-center justify-center rounded-full bg-white text-border/100 transition-colors',
                  isSelected && 'bg-primary text-white',
                )}
              >
                <Check size={14} strokeWidth={2} />
              </span>
            </button>
            {index < members.length - 1 && <div className="ml-8 h-px shrink-0 bg-lightBorder" />}
          </React.Fragment>
        )
      })}
      {isFetchingNextPage && <p className="py-1 text-[13px] font-medium text-muted">Loading more members...</p>}
      {hasNextPage && <div ref={endOfListRef} className="h-px shrink-0" aria-hidden="true" />}
    </div>
  )
}

function MemberListFallback() {
  return (
    <div className="flex flex-col gap-3 py-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="size-10 shrink-0 animate-pulse rounded-full bg-accent" />
          <div className="flex flex-1 flex-col gap-2">
            <div className="h-3.5 w-40 animate-pulse rounded bg-accent" />
            <div className="h-3 w-52 animate-pulse rounded bg-accent" />
          </div>
        </div>
      ))}
    </div>
  )
}

export default function LeadDistributionMemberPicker({
  enterpriseId,
  allMemberIds = [],
  companyType,
  selectedUserIds,
  onSelectedUserIdsChange,
}: LeadDistributionMemberPickerProps) {
  const [searchValue, setSearchValue] = useState('')
  const [loadedUserIds, setLoadedUserIds] = useState<number[]>([])
  const loadedUserIdsRef = useRef(loadedUserIds)

  useEffect(() => {
    loadedUserIdsRef.current = loadedUserIds
  }, [loadedUserIds])

  const handleLoadedUserIdsChange = useCallback((userIds: number[]) => {
    setLoadedUserIds(userIds)
  }, [])

  const plural = getMemberPluralLabel(companyType)
  const count = selectedUserIds.length

  const hasUsersLoaded = loadedUserIds.length > 0
  const allSelected = hasUsersLoaded && allMemberIds.length > 0 && selectedUserIds.length === allMemberIds.length

  const handleToggleUserId = (userId: number) => {
    const next = new Set(selectedUserIds)
    if (next.has(userId)) {
      next.delete(userId)
    } else {
      next.add(userId)
    }
    onSelectedUserIdsChange([...next])
  }

  const handleSelectAll = () => {
    onSelectedUserIdsChange(allMemberIds)
  }

  const handleDeselectAll = () => {
    onSelectedUserIdsChange([])
  }

  if (enterpriseId <= 0) {
    return (
      <div className="rounded-2xl bg-accent p-5">
        <p className="text-[13px] font-medium text-muted">Members are unavailable for this enterprise.</p>
      </div>
    )
  }

  return (
    <div className="flex h-full max-h-[680px] min-h-[420px] flex-col rounded-2xl bg-accent p-5">
      <div className="mb-4 flex items-start justify-between gap-3 px-[10px] pt-1">
        <p className="text-[15px] font-semibold leading-tight tracking-tight text-primary">
          {count} {plural}
          {count == 1 ? '' : 's'} selected
        </p>
        <button
          type="button"
          onClick={allSelected ? handleDeselectAll : handleSelectAll}
          disabled={!hasUsersLoaded}
          className={cn(
            'shrink-0 text-sm font-medium underline underline-offset-2 transition-opacity',
            !hasUsersLoaded ? 'cursor-not-allowed text-muted opacity-40' : 'text-primary hover:opacity-80',
          )}
        >
          {allSelected ? 'Deselect all' : 'Select all'}
        </button>
      </div>

      <div className="mb-[15px] flex h-[45px] w-full items-center gap-1.5 rounded-full bg-white pl-[15px] pr-2">
        <Search size={16} className="shrink-0 text-primary" />
        <input
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder="Search by name"
          className="size-full border-none bg-transparent text-[14px] font-medium leading-[1.6] tracking-[-0.14px] text-primary outline-none placeholder:text-muted"
        />
        {searchValue ? (
          <button
            type="button"
            onClick={() => setSearchValue('')}
            className="flex size-8 shrink-0 items-center justify-center rounded-full bg-accent text-primary transition-colors hover:bg-primary hover:text-white"
            aria-label="Clear search"
          >
            <X size={16} />
          </button>
        ) : null}
      </div>

      <Suspense fallback={<MemberListFallback />}>
        <MemberListInner
          enterpriseId={enterpriseId}
          searchValue={searchValue}
          selectedUserIds={selectedUserIds}
          onToggleUserId={handleToggleUserId}
          onLoadedUserIdsChange={handleLoadedUserIdsChange}
        />
      </Suspense>
    </div>
  )
}
